import type { PublicEvent } from "@/services/api/publicEvents";

type NamedEvent = Pick<
  PublicEvent,
  "name" | "includesWedding" | "includesBaptism" | "starBride" | "starGroom" | "starBaptism" | "starBaptism2"
>;

const clean = (value: string | null | undefined): string => (value ?? "").trim();

/**
 * What the hero of every template shows as the event's name (JUB-181): the
 * couple for a wedding ("Maria & Vasile"), the child or children for a baptism
 * ("Sofia", "Sofia & Maria"), both for a combined event ("Maria & Vasile,
 * Sofia"). A single known name stands alone; when no name is set the event's
 * own name is used, so the document title is never empty.
 */
export function publicEventDisplayName(event: NamedEvent): string {
  const includesWedding = event.includesWedding ?? true;
  const includesBaptism = event.includesBaptism ?? false;
  const couple = [clean(event.starBride), clean(event.starGroom)].filter(Boolean).join(" & ");
  const children = [clean(event.starBaptism), clean(event.starBaptism2)].filter(Boolean).join(" & ");
  const parts: string[] = [];
  if (includesWedding && couple) parts.push(couple);
  if (includesBaptism && children) parts.push(children);
  if (parts.length === 0 && couple) parts.push(couple);
  if (parts.length === 0 && children) parts.push(children);
  return parts.join(", ") || clean(event.name);
}

/** Document title of a public event page: the display name, then the brand. */
export const publicEventPageTitle = (event: NamedEvent): string => `${publicEventDisplayName(event)} | Jubiel`;
